// Session-store types — shared between the store wiring, the
// artifacts reducer, and the event reducer. Extracted from
// sessionStore.ts to keep that file under the 400-LOC ceiling
// (docs/plans/13 §M3).

import type { LoopEvent } from "@/lib/events";

export type SessionTier = "free" | "paid";

export type MedicalRecordStatus = "pending" | "uploaded" | "extracted" | "opted_out";

/** One section of the backend-owned letter plan. */
export interface PlannedSection {
  sectionType: string;
  variant: string;
  title: string;
}

/** The deterministic letter shape the backend decided. The FE renders +
 *  paces against this instead of a hardcoded six-section flow. */
export interface LetterPlan {
  mode: "full" | "no_record" | "blocked";
  sections: PlannedSection[];
}

export type ChatRole = "user" | "assistant" | "system";

export interface ChatMessage {
  id: string;
  role: ChatRole;
  content: string;
  timestamp: string;
  pending?: boolean;
}

export interface ToolActivity {
  id: string;
  toolName: string;
  status: "running" | "ok" | "denied" | "error";
  arguments: Record<string, unknown>;
  result?: Record<string, unknown>;
  reason?: string;
  errorType?: string;
  startedAt: string;
  completedAt?: string;
  durationMs?: number;
}

export interface SessionConnection {
  sessionId: string | null;
  tier: SessionTier;
  expiresAt: string | null;
  connected: boolean;
  reconnecting: boolean;
  hasPaymentEmail: boolean;
  stripeCustomerId: string | null;
  stripeCheckoutSessionId: string | null;
  recoveryAccessToken: string | null;
  // The legal-bundle version the user accepted in THIS session. Session-scoped
  // by design: bootCleanup wipes the store on F5 (= fresh backend session = a
  // fresh affirmative acceptance), so this is never a replayed browser flag.
  consentBundleVersion: string | null;
  // The pre-payment "my medical record matters" acknowledgment the user
  // checked in THIS session (see lib/recordImportanceAck.ts). Session-scoped
  // for the same reason as consentBundleVersion — bootCleanup wipes it on F5,
  // so it is always a fresh affirmative acceptance, never a replayed flag.
  recordImportanceAckVersion: string | null;
  // The medical-record decision + the letter plan it produces, synced from
  // getSession (connect / after each turn) and the opt-out response.
  medicalRecordStatus: MedicalRecordStatus;
  letterPlan: LetterPlan | null;
  // True when a coverage-policy letter would lean on thin/absent policy.
  weakPolicy: boolean;
  // The member's own attributed statement (coverage-policy letters), or null.
  memberStatement: string | null;
  // Derived on the backend: every planned section verified at its current
  // body. The wizard shows a "verify the letter" step before export.
  citationsVerified: boolean;
}

export interface ArtifactState {
  denialDetails: Record<string, unknown> | null;
  probability: Record<string, unknown> | null;
  policyHits: Record<string, unknown>[] | null;
  /** Sections keyed by section_type (intro / medical_necessity / …).
   *  Re-generating the same section replaces, not appends. */
  appealSections: Record<string, Record<string, unknown>>;
  /** RefineAppealSection stages here, keyed by section_type. The Letter
   *  tab renders a diff card per pending entry with Accept / Revert
   *  buttons that dispatch chat commands the agent translates into
   *  AcceptRefinement / RevertRefinement tool calls. */
  pendingRefinements: Record<string, Record<string, unknown>>;
  references: Record<string, unknown>[] | null;
  /** PlanEvidence result. The agent calls PlanEvidence after extraction
   *  to produce a goal-mapped retrieval plan; this surfaces the goals
   *  + planned searches in the Evidence tab so the user can see what
   *  the system intends to look for. */
  evidencePlan: Record<string, unknown> | null;
  /** Findings returned by the policy_researcher specialist via AgentTool.
   *  Used by the Evidence tab to show what policy passages the system
   *  pulled, grouped by source_kind. */
  policyResearcherFindings: Record<string, unknown> | null;
  /** Findings returned by the medical_record_extractor specialist via
   *  AgentTool. Used by the Evidence tab to confirm the medical record
   *  was actually read and to show what was extracted. */
  medicalRecordFindings: Record<string, unknown> | null;
  /** Recent SearchMedicalRecord hits (capped at 30, newest first). The
   *  Evidence tab shows these so the user can see the actual snippets
   *  retrieved by each query the agent ran against their record. */
  medicalRecordSearchHits: Record<string, unknown>[];
  /** Outcome of the most recent VerifyCitations run. When a run fails,
   *  the wizard's verify stage swaps its CTA to a "fix the flagged
   *  section(s)" refine action instead of re-running the same check on
   *  the same text. Cleared whenever the letter changes (a section is
   *  generated or a refinement accepted) — the snapshot is stale then. */
  lastVerification: { verified: boolean; failedSections: string[] } | null;
  exports: { pdf?: string; docx?: string };
}

export interface SessionState {
  connection: SessionConnection;
  messages: ChatMessage[];
  tools: ToolActivity[];
  artifacts: ArtifactState;
  pendingTurn: boolean;

  setConnection: (patch: Partial<SessionConnection>) => void;
  setSession: (session: {
    sessionId: string;
    tier: SessionTier;
    expiresAt: string;
    stripeCustomerId?: string | null;
    stripeCheckoutSessionId?: string | null;
    recoveryAccessToken?: string | null;
    hasPaymentEmail?: boolean;
    medicalRecordStatus?: MedicalRecordStatus;
    letterPlan?: LetterPlan | null;
    weakPolicy?: boolean;
    memberStatement?: string | null;
    citationsVerified?: boolean;
  }) => void;
  resetSession: () => void;

  appendMessage: (message: ChatMessage) => void;
  setPendingTurn: (pending: boolean) => void;
  /** Forcibly clear pendingTurn AND surface a system message in chat.
   *  Called when the WS drops mid-turn (non-1000 close, reconnect
   *  give-up). Without this the composer stays disabled with no
   *  visible recovery path. */
  clearPendingTurn: (reason: string) => void;

  applyEvent: (event: LoopEvent) => void;
}

export const emptyArtifacts: ArtifactState = {
  denialDetails: null,
  probability: null,
  policyHits: null,
  appealSections: {},
  pendingRefinements: {},
  references: null,
  evidencePlan: null,
  policyResearcherFindings: null,
  medicalRecordFindings: null,
  medicalRecordSearchHits: [],
  lastVerification: null,
  exports: {},
};

export const emptyConnection: SessionConnection = {
  sessionId: null,
  tier: "free",
  expiresAt: null,
  connected: false,
  reconnecting: false,
  hasPaymentEmail: false,
  stripeCustomerId: null,
  stripeCheckoutSessionId: null,
  recoveryAccessToken: null,
  consentBundleVersion: null,
  recordImportanceAckVersion: null,
  medicalRecordStatus: "pending",
  letterPlan: null,
  weakPolicy: false,
  memberStatement: null,
  citationsVerified: false,
};

export function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `id_${Math.random().toString(36).slice(2)}_${Date.now()}`;
}
